/*
 * Jira Ticket:
 * Created Date: Thu, 26th Mar 2020, 11:44:56 am
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useState, useEffect } from 'react';
import inflection from 'inflection';
import { useHistory } from 'react-router-dom';
import { useMediaQuery } from '@material-ui/core';
import { ViewList as DefaultIcon } from '@material-ui/icons';
import { connect, useSelector, useDispatch } from 'react-redux';
import {
  fetchEnd,
  useNotify,
  fetchStart,
  getResources,
  useTranslate,
  MenuItemLink,
  useDataProvider,
} from 'react-admin';
import {
  GridOn,
  Business,
  Schedule,
  Assignment,
  BusinessCenter,
  AssignmentLate,
} from '@material-ui/icons';

import { ConfirmModal as Modal } from '../../Components';
import {
  isSuperAdmin,
  isSuperManager,
  isApprover,
  isBuilder,
} from 'utils/roles';
import getPendingInvites from '../../utils/getPendingInvites';
import { PendingInvitesModalContent } from '../../resources/Inspection/ModalContent';

const translatedResourceName = (resource, translate) =>
  translate(`resources.${resource.name}.name`, {
    smart_count: 2,
    _:
      resource.options && resource.options.label
        ? translate(resource.options.label, {
            smart_count: 2,
            _: resource.options.label,
          })
        : inflection.humanize(inflection.pluralize(resource.name)),
  });

const menuOrder = [
  'Project',
  'Site',
  'User',
  'Subcontractor',
  'Contract',
  'Tower',
  'Organisation',
  'TowerType',
  'Template',
  'Advert',
  'Region',
  'SubscriptionTier',
  'Configuration',
];

const dashboardDict = {
  'Assembled Towers': {
    title: 'Assembled',
    to: 'Inspection?displayedFilters=%7B%7D&filter=%7B%7D&order=ASC&page=1&perPage=25&sort=id',
    icon: <GridOn style={{ color: '#E55B19' }} />,
  },
  'Inspections Awaiting Approval': {
    title: 'Need Approval',
    to: 'Inspection?displayedFilters=%7B"cmsStatus"%3Atrue%7D&filter=%7B"cmsStatus"%3A"INSPECTED"%7D',
    icon: <Assignment style={{ color: '#E55B19' }} />,
  },
  Projects: {
    title: 'Projects',
    to: 'Project',
    icon: <BusinessCenter style={{ color: '#E55B19' }} />,
  },
  'Expired Inspections': {
    title: 'Expired',
    to: 'Inspection?displayedFilters=%7B"cmsStatus"%3Atrue%7D&filter=%7B"cmsStatus"%3A"REQUIRES_REINSPECTION"%7D',
    icon: <Schedule style={{ color: '#E55B19' }} />,
  },
  'Rejected Inspections': {
    title: 'Rejected',
    to: 'Inspection?displayedFilters=%7B"cmsStatus"%3Atrue%7D&filter=%7B"cmsStatus"%3A"DO_NOT_USE"%7D',
    icon: <AssignmentLate style={{ color: '#E55B19' }} />,
  },
  Sites: {
    title: 'Sites',
    to: 'Site',
    icon: <Business style={{ color: '#E55B19' }} />,
  },
};

const Menu = ({ onMenuClick, logout, ...props }) => {
  const { userPermissions } = props;

  const notify = useNotify();
  const history = useHistory();
  const dispatch = useDispatch();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const resources = useSelector(getResources);
  const isApproved = isApprover(userPermissions);
  const open = useSelector((state) => state.admin.ui.sidebarOpen);
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const userId = localStorage.getItem('userSub');

  const [isLoading, setIsLoading] = useState(true);
  const [statistics, setStatistics] = useState([]);
  const [pendingInvites, setPendingInvites] = useState();
  const [modalVisible, setModalVisible] = useState(false);

  const resourcesForMenu = resources
    .filter(
      (resource) =>
        // resource.name !== 'Tower' &&
        resource.name !== 'Inspection' &&
        // resource.name !== 'Project' &&
        // resource.name !== 'Site' &&
        resource.name !== 'Employment' &&
        // resource.name !== 'Subcontract' &&
        resource.name !== 'OrganisationSearch' &&
        // resource.name !== 'Qualification' && // removed due to PAS-608
        resource.name !== 'Country' &&
        resource.name !== 'Locale' &&
        resource.name !== 'AccessDenied'
    )
    .sort((a, b) => {
      return menuOrder.indexOf(a.name) - menuOrder.indexOf(b.name);
    });

  const inspectionMenuItem = () => {
    const resource = resources.find((r) => r.name === 'Inspection');
    if (resource) {
      return (
        <MenuItemLink
          key={resource.name}
          to={`/${resource.name}`}
          primaryText={
            translatedResourceName(resource, translate) || resource.name
          }
          leftIcon={resource.icon ? <resource.icon /> : <DefaultIcon />}
          onClick={onMenuClick}
          sidebarIsOpen={open}
        />
      );
    } else {
      return null;
    }
  };

  useEffect(() => {
    let isSubscribed = true;
    const getDashboardStatistics = () => {
      const userSub = localStorage.getItem('userSub');
      const initialAppViewDone = localStorage.getItem('initialAppViewDone');

      if (!initialAppViewDone && isApproved) {
        history.push('/inspection');
        localStorage.setItem('initialAppViewDone', '1');
      }
      if (userSub) {
        dispatch(fetchStart());
        // array called statistics = [ icon, name, value ]
        return dataProvider('GET_STATISTICS', 'Dashboard', {})
          .then((result) => {
            dispatch(fetchEnd());
            if (isSubscribed) {
              const itemOrder = [
                'Assembled Towers',
                'Inspections Awaiting Approval',
                'Expired Inspections',
                'Rejected Inspections',
                'Projects',
                'Sites',
              ];
              const customOrderStatistics = result.data.statistics.sort(
                (a, b) => {
                  return itemOrder.indexOf(a.name) - itemOrder.indexOf(b.name);
                }
              );
              setStatistics(customOrderStatistics);
              setIsLoading(false);
            }
          })
          .catch((error) => {
            dispatch(fetchEnd());
            if (error.graphQLErrors.length > 0) {
              // only return an error if it's graphql error
              setIsLoading(false);
              return notify('error.dashboard.unableToGetStats', 'warning');
            }
          });
      }
    };
    getDashboardStatistics();
    return () => (isSubscribed = false);
  }, [
    dispatch,
    dataProvider,
    notify,
    setStatistics,
    setIsLoading,
    history,
    isApproved,
  ]);

  const useMountEffect = (fn) => useEffect(fn, []);

  useMountEffect(
    getPendingInvites(userId, {
      dataProvider,
      dispatch,
      setPendingInvites,
      notify,
    })
  );

  useEffect(() => {
    const invitesChecked = localStorage.getItem('INVITES_MODAL_SHOWN');
    if (invitesChecked !== 'true' && pendingInvites?.length > 0) {
      setModalVisible(true);
      localStorage.setItem('INVITES_MODAL_SHOWN', 'true');
    }
  }, [pendingInvites]);

  return (
    <>
      {props && props.formState && (
        <div>
          {inspectionMenuItem()}
          {resourcesForMenu.map((resource) => {
            if (
              resource.name === 'TowerType' ||
              resource.name === 'Template' ||
              resource.name === 'Region' ||
              resource.name === 'Advert' ||
              resource.name === 'SubContract' ||
              resource.name === 'SubscriptionTier' ||
              resource.name === 'Configuration'
            ) {
              return (
                isSuperAdmin(userPermissions) && (
                  <MenuItemLink
                    key={resource.name}
                    to={`/${resource.name}`}
                    primaryText={
                      translatedResourceName(resource, translate) ||
                      resource.name
                    }
                    leftIcon={
                      resource.icon ? <resource.icon /> : <DefaultIcon />
                    }
                    onClick={onMenuClick}
                    sidebarIsOpen={open}
                  />
                )
              );
            }

            if (resource.name === 'Tower') {
              return (
                isSuperManager(userPermissions) && (
                  <MenuItemLink
                    key={resource.name}
                    to={`/${resource.name}`}
                    primaryText={
                      translatedResourceName(resource, translate) ||
                      resource.name
                    }
                    leftIcon={
                      resource.icon ? <resource.icon /> : <DefaultIcon />
                    }
                    onClick={onMenuClick}
                    sidebarIsOpen={open}
                  />
                )
              );
            }

            if (resource.name === 'Inspection') {
              return (
                isBuilder(userPermissions) && (
                  <MenuItemLink
                    key={resource.name}
                    to={`/${resource.name}`}
                    primaryText={
                      translatedResourceName(resource, translate) ||
                      resource.name
                    }
                    leftIcon={
                      resource.icon ? <resource.icon /> : <DefaultIcon />
                    }
                    onClick={onMenuClick}
                    sidebarIsOpen={open}
                  />
                )
              );
            }

            return (
              <MenuItemLink
                key={resource.name}
                to={`/${resource.name}`}
                primaryText={
                  translatedResourceName(resource, translate) || resource.name
                }
                leftIcon={resource.icon ? <resource.icon /> : <DefaultIcon />}
                onClick={onMenuClick}
                sidebarIsOpen={open}
              />
            );
          })}
          <div style={{ marginTop: 32 }}>
            {statistics.length > 0 &&
              statistics.map((it) => {
                return (
                  <MenuItemLink
                    key={it.name}
                    to={`/${dashboardDict[it.name].to}`}
                    primaryText={`${it.value} - ${
                      dashboardDict[it.name].title
                    }`}
                    leftIcon={
                      it.icon ? dashboardDict[it.name].icon : <DefaultIcon />
                    }
                    onClick={onMenuClick}
                    sidebarIsOpen={open}
                  />
                );
              })}
          </div>
          {isXSmall && logout}
        </div>
      )}
      <Modal
        isOpen={modalVisible}
        title=""
        content={<PendingInvitesModalContent translate={translate} />}
        cancel="resources.Organisation.fields.pendingInvitesCancel"
        onClose={() => setModalVisible(false)}
        confirm="resources.Organisation.fields.pendingInvitesConfirm"
        onConfirm={() => null}
        confirmLink
        confirmLinkTo={`User/${userId}/2`}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  formState: state,
  userPermissions: JSON.parse(localStorage.getItem('org_permissions')),
});

export default connect(
  mapStateToProps,
  {} // Avoid connect passing dispatch in props
)(Menu);
